import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import styled from "styled-components"

import { IDLE, PENDING, LIST, ADD, EDIT } from "const"
import { StateType, deepCopy, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_RRS_CATEGORY,
    StoreType,
    RrsCategoryFilters,
    AccountSettings,

    LoadAccountSettings,
    SaveAccountSettings,

    CopyDefaultRrsCategories,
    LoadAllRrsCategories,
    SortRrsCategories,
    PrepareRrsCategoryNestedIds,
    ChangeParentsRrsCategory,
    DeleteRrsCategories,
    SelectParentRrsTemplateLabelCategories,
    SelectParentRrsTemplateLabels,
    SetRrsTemplateRiskMapGenerated,

    setSystemAccountSettings,
    setRrsCategoryList,
    setRrsCategoryChildParent,
    setRrsCategoryIds,
    setRrsCategoryFilters,
    setRrsCategoryShowConfirmDelete,
    setRrsCategoryReloadList,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    Table,
    TableSearchWrapper,
    TableNestedIds,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
    CheckButton,
    SpinnerIcon,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import RrsCategorySearch from "client/pages/search/rrsCategorySearch"
import Columns from "./columns"

export const TemplateWarningWrapper = styled.div`
    margin: 15px;
    color: ${(props) => props.theme.rrsCategoryWarningColor};
`

const List = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const { defaultCategoriesAssigned } = project.id ? projectSettings.rrs : accountSettings.rrs

    const baseRoute = useSelector((state: StoreType) => state.rrsCategory.baseRoute)
    const categoryList = useSelector((state: StoreType) => state.rrsCategory.categoryList)
    const categoryIds = useSelector((state: StoreType) => state.rrsCategory.categoryIds)
    const filters = useSelector((state: StoreType) => state.rrsCategory.filters)
    const reloadList = useSelector((state: StoreType) => state.rrsCategory.reloadList)

    const activeCategory = useSelector((state: StoreType) => state.rrsCategory.active)
    const showConfirmDelete = useSelector((state: StoreType) => state.rrsCategory.showConfirmDelete)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [importState, setImportState] = useState<StateType>(IDLE)

    const [deleteNested, setDeleteNested] = useState(false)
    const [deleteNestedLoadState, setDeleteNestedLoadState] = useState<StateType>(IDLE)
    const [deleteCategoryIds, setDeleteCategoryIds] = useState<number[]>([])
    const [templateIds, setTemplateIds] = useState<number[]>([])
    const [templateWarning, setTemplateWarning] = useState<any>(null)
    const [deleteState, setDeleteState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (userData.accountId === 1) return

        // copy list of categories only to account level
        if (!project.id && !accountSettings.rrs.defaultCategoriesAssigned) {
            setImportState(PENDING)
            LoadAccountSettings(userData.accountId)
            .then((settings) => {
                if (!settings.rrs.defaultCategoriesAssigned) {
                    CopyDefaultRrsCategories(userData.accountId, null, false)
                    .then(() => {
                        const newSettings: AccountSettings = deepCopy(settings)
                        newSettings.rrs.defaultCategoriesAssigned = true

                        SaveAccountSettings(userData.accountId, newSettings)
                        .then(() => {
                            dispatch(setSystemAccountSettings(newSettings))
                            setImportState(IDLE)
                        })
                    })
                } else {
                    setImportState(IDLE)
                }
            })
        }
    }, [])

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllRrsCategories(userData.accountId, project.id).then((res) => {
            dispatch(setRrsCategoryList(res.categoryList))
            dispatch(setRrsCategoryChildParent(res.childParent))
            setLoadState(IDLE)
        })
    }, [reloadList, defaultCategoriesAssigned])

    useEffect(() => {
        const categoriesFiltered = SortRrsCategories(Object.values(categoryList), filters)
        const groupedIds = PrepareRrsCategoryNestedIds(categoriesFiltered)
        dispatch(setRrsCategoryIds(groupedIds))
    }, [categoryList, filters])

    const onFilter = (newFilters: RrsCategoryFilters) => {
        dispatch(setRrsCategoryFilters(newFilters))
    }

    const onCancelDelete = () => {
        dispatch(setRrsCategoryShowConfirmDelete(false))
    }

    useEffect(() => {
        if (!activeCategory) {
            setDeleteCategoryIds([])
            setTemplateIds([])
            setTemplateWarning(null)
            return
        }

        setDeleteNestedLoadState(PENDING)
        const deleteIds: number[] = [activeCategory.id]
        if (deleteNested) {
            // add nested to delete
            const groupedIds = PrepareRrsCategoryNestedIds(Object.values(categoryList))
            const addNestedToDelete = (nestedIds: readonly TableNestedIds[], addToDelete: boolean) => {
                nestedIds.forEach((row) => {
                    const categoryId = Number(row.id)
                    if (addToDelete) {
                        deleteIds.push(categoryId)
                        addNestedToDelete(row.nestedIds, true)
                    } else {
                        addNestedToDelete(row.nestedIds, categoryId === activeCategory.id)
                    }
                })
            }
            if (groupedIds[0]) addNestedToDelete(groupedIds[0].nestedIds, false) // where groupedIds[0] - top level group
        }
        setDeleteCategoryIds(deleteIds)

        // select templateIds that use removed categories
        Promise.all([
            SelectParentRrsTemplateLabelCategories(deleteIds),
            SelectParentRrsTemplateLabels(deleteIds),
        ]).then(([templates1, templates2]) => {
            const templates = {
                ...templates1,
                ...templates2,
            }

            const ids: number[] = Object.keys(templates) as unknown as number[]
            setTemplateIds(ids)

            const warnings: any[] = []
            Object.values(templates).forEach((row) => {
                warnings.push(<li key={row.id}>{row.name}</li>)
            })
            if (warnings.length) {
                setTemplateWarning(
                    <TemplateWarningWrapper>
                        <div>{lang.categoryUsedIn}</div>
                        <ul>{warnings}</ul>
                    </TemplateWarningWrapper>,
                )
            } else {
                setTemplateWarning(null)
            }

            setDeleteNestedLoadState(IDLE)
        })
    }, [activeCategory, deleteNested, categoryList])

    const onConfirmDelete = () => {
        setDeleteState(PENDING)
        const promiseArray: Promise<any>[] = []

        if (!deleteNested) {
            // update parents
            promiseArray.push(
                ChangeParentsRrsCategory(activeCategory.id, activeCategory.categoryId, userData.accountId, project.id),
            )
        }

        // set riskMapGenerated to "false"
        SetRrsTemplateRiskMapGenerated(templateIds, false).then(() => {
            Promise.all(promiseArray).then(() => {
                DeleteRrsCategories({
                    deleteIds: deleteCategoryIds,
                    accountId: userData.accountId,
                    projectId: project.id,
                }).then(() => {
                    setDeleteState(IDLE)
                    dispatch(setRrsCategoryShowConfirmDelete(false))

                    dispatch(setRrsCategoryReloadList(false))
                    setTimeout(() => {
                        dispatch(setRrsCategoryReloadList(true))
                    }, 0)
                })
            })
        })
    }

    const columns = Columns()

    const listAccess = !project.id
        ? hasAccess(PRM_RRS_CATEGORY, LIST)
        : hasProjectAccess(PRM_RRS_CATEGORY, LIST)

    const addAccess = !project.id
        ? hasAccess(PRM_RRS_CATEGORY, ADD)
        : hasProjectAccess(PRM_RRS_CATEGORY, ADD)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.categories}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    (loadState === PENDING || importState === PENDING)
                    && <Loader />
                }
                <TableSearchWrapper>
                    <RrsCategorySearch
                        id="rrsCategoryList"
                        rightOffset={addAccess}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && (
                            <Button
                                variant="success"
                                onClick={() => dispatch(addModal({ url: `${baseRoute}/${EDIT}/0` }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={categoryList}
                    dataGroupedIds={categoryIds}
                    rowSize="sm"
                    showPagination
                />

                <ConfirmWrapper show={showConfirmDelete} onClose={onCancelDelete}>
                    <Confirm align="start">
                        <ConfirmTitle>{activeCategory && activeCategory.name}</ConfirmTitle>
                        <ConfirmDescription>{lang.deleteCategoryConfirm}</ConfirmDescription>

                        <CheckButton
                            checked={deleteNested}
                            onChange={() => setDeleteNested(!deleteNested)}
                            title={lang.deleteCategoryNested}
                        />

                        <ConfirmDescription>{templateWarning}</ConfirmDescription>

                        <ConfirmBottom>
                            <Button
                                row
                                disabled={deleteNestedLoadState === PENDING}
                                onClick={onCancelDelete}
                            >
                                {lang.cancel}
                            </Button>
                            <Button
                                row
                                disabled={deleteNestedLoadState === PENDING}
                                variant="success"
                                onClick={onConfirmDelete}
                            >
                                {deleteState === PENDING ? <SpinnerIcon /> : lang.confirm}
                            </Button>
                        </ConfirmBottom>
                    </Confirm>
                </ConfirmWrapper>
            </PageContent>
        </Container>
    )
}
export default List
